import type { ReactNode, ChangeEvent } from 'react';
import track, { useTracking } from 'react-tracking';
import { ToggleLabel, ToggleWrapper, ToggleBase, ToggleSwitch } from './styled';

export interface Props {
  name: string;
  displayType?: 'block' | 'inline-block';
  label?: ReactNode;
  value: boolean;
  invert?: boolean;
  tabIndex?: number;
  readOnly?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  trackingProps?: Record<string, string>;
  onBlur?: () => void;
  onFocus?: () => void;
}

const Toggle = ({
  name,
  value,
  label,
  invert,
  readOnly,
  disabled,
  displayType,
  tabIndex,
  onChange,
  autoFocus,
  onBlur,
  onFocus,
}: Props) => {
  const { trackEvent } = useTracking();
  return (
    <ToggleWrapper disabled={disabled} invert={invert}>
      <ToggleLabel
        data-cy-id={`toggle-label-${name}`}
        displayType={displayType as string}
      >
        {label}
        <input
          data-cy-id={`toggle-btn-${name}`}
          name={name}
          type="checkbox"
          tabIndex={tabIndex || 0}
          onChange={(e) => {
            onChange && onChange(e);
            trackEvent({
              action: 'toggle',
              active: e.target.checked,
            });
          }}
          autoFocus={autoFocus}
          checked={value}
          disabled={disabled}
          readOnly={readOnly}
          onBlur={onBlur}
          onFocus={onFocus}
        />
        <ToggleBase invert={invert}>
          <ToggleSwitch />
        </ToggleBase>
        <span />
      </ToggleLabel>
    </ToggleWrapper>
  );
};

Toggle.defaultProps = {
  label: null,
  displayType: 'inline-block',
  disabled: false,
};

export default track(({ name, trackingProps }) => ({
  type: 'track',
  modifier: name || undefined,
  ...trackingProps,
}))(Toggle) as typeof Toggle;
